// Client-side phone-number validation for #284. Mirrors the FFCustomerApp
// validator and returns the same `PhoneValidationSuggestion` shape the
// server emits, so the same UI wiring (error + "Use this number" button)
// renders consistently across web and mobile. Server-side validation
// remains the authoritative backstop.
//
// MVP coverage: empty, non-numeric, double-country-code (with suggestion),
// TOO_SHORT, TOO_LONG, generic invalid. Landline detection is deferred to
// the server (needs `libphonenumber-js/max`, +50KB).

import { parsePhoneNumber, validatePhoneNumberLength } from "libphonenumber-js";
import { PhoneValidationSuggestion } from "./errorMessages";

export type PhoneValidationResult =
  | { valid: true }
  | {
      valid: false;
      userMessage: string;
      suggestion?: PhoneValidationSuggestion;
    };

const GENERIC_INVALID =
  "Please check the number you entered \u2014 we couldn\u2019t recognise it as a valid mobile number.";

// The validator must never throw — it runs inline in the submit handler
// and an unhandled exception there would unmount the form. Every call into
// libphonenumber-js is wrapped: parse and isValid swallow any error type
// (not just ParseError) and fall back to a friendly generic message.

function safeParse(e164: string) {
  try {
    return parsePhoneNumber(e164);
  } catch {
    return undefined;
  }
}

function safeIsValid(parsed: ReturnType<typeof parsePhoneNumber> | undefined) {
  if (!parsed) return false;
  try {
    return parsed.isValid();
  } catch {
    return false;
  }
}

function safeFormatInternational(
  parsed: NonNullable<ReturnType<typeof parsePhoneNumber>>,
  fallback: string,
): string {
  try {
    return parsed.formatInternational();
  } catch {
    return fallback;
  }
}

function safeLengthCheck(e164: string): string | undefined {
  try {
    return validatePhoneNumberLength(e164);
  } catch {
    return undefined;
  }
}

export function validatePhoneClientSide(
  callingCode: string,
  phoneNumber: string,
): PhoneValidationResult {
  try {
    const trimmed = phoneNumber.trim();

    if (!trimmed) {
      return { valid: false, userMessage: "Please enter your phone number." };
    }

    // Strip common separators users sometimes paste in. Anything left that
    // isn't a digit means the input has letters or unusual punctuation.
    const cleaned = trimmed.replace(/[\s\-()]/g, "");
    if (!/^[0-9]+$/.test(cleaned)) {
      return {
        valid: false,
        userMessage:
          "Phone numbers contain digits only \u2014 please check the number you entered.",
      };
    }

    // Double-country-code detection. If the subscriber begins with the
    // calling code AND stripping it yields a valid number, surface the
    // stripped version as a suggestion. Customer A's case (#284):
    // country=+44, subscriber=447398901055 -> suggest 7398901055.
    if (cleaned.startsWith(callingCode)) {
      const stripped = cleaned.slice(callingCode.length);
      if (stripped.length > 0) {
        const candidate = safeParse(`+${callingCode}${stripped}`);
        if (candidate && safeIsValid(candidate)) {
          const fallbackDisplay = `+${callingCode} ${stripped}`;
          return {
            valid: false,
            userMessage: `It looks like the country code was entered twice. Did you mean ${safeFormatInternational(
              candidate,
              fallbackDisplay,
            )}?`,
            suggestion: {
              rejectionReason: "DOUBLE_COUNTRY_CODE",
              code: "phone-validation-rejected",
              suggestedE164: candidate.number,
              suggestedCallingCode: callingCode,
              suggestedPhoneNumber: stripped,
            },
          };
        }
      }
    }

    const e164 = `+${callingCode}${cleaned}`;

    const lengthIssue = safeLengthCheck(e164);
    if (lengthIssue === "TOO_SHORT") {
      return {
        valid: false,
        userMessage:
          "That looks too short \u2014 please check the number you entered.",
      };
    }
    if (lengthIssue === "TOO_LONG") {
      return {
        valid: false,
        userMessage:
          "That looks too long \u2014 please check if you accidentally added extra digits.",
      };
    }

    // NOTE — do NOT add a client-side "you left the leading 0 on" rejection
    // here. It looks like an obvious improvement and it is a regression: the
    // API strips leading zeros from the subscriber before it looks the member
    // up (`AuthService.cleanNumberString(phone, true)`), so `+44` + `07400123456`
    // authenticates perfectly well. libphonenumber agrees — it normalises the
    // trunk prefix for GB/IE/AU/NZ, so `parsePhoneNumber` already returns valid
    // and this branch is never reached for them. Adding a rejection would only
    // fire for countries with no trunk prefix (US-style), where it would block
    // a login the server would have handled, because an invalid result makes
    // `onSubmitSms` return before the API is ever called.
    const parsed = safeParse(e164);
    if (!safeIsValid(parsed)) {
      return { valid: false, userMessage: GENERIC_INVALID };
    }

    return { valid: true };
  } catch {
    // Outermost safety net — should be unreachable but guarantees the
    // form never crashes due to a validator bug or unforeseen library
    // behaviour on malformed input.
    return { valid: false, userMessage: GENERIC_INVALID };
  }
}
